/**
 * MyPoint - класс для работы с 2D точками
 * JavaScript версия MyPoint из VectorGeometryLib
 */
class MyPoint {
    /**
     * Конструктор точки
     * @param {number} x - координата X
     * @param {number} y - координата Y
     */
    constructor(x = 0, y = 0) {
        this.x = x;
        this.y = y;
    }

    /**
     * Статическая константа для сравнения с плавающей точкой
     */
    static get EPSILON() {
        return 1e-6;
    }

    /**
     * Нулевая точка
     */
    static get ZERO() {
        return new MyPoint(0, 0);
    }

    /**
     * Получить координату X
     */
    get X() {
        return this.x;
    }

    /**
     * Получить координату Y
     */
    get Y() {
        return this.y;
    }

    /**
     * Квадрат длины вектора от начала координат
     */
    get LengthSquared() {
        return this.x * this.x + this.y * this.y;
    }

    /**
     * Длина вектора от начала координат
     */
    get Length() {
        return Math.sqrt(this.LengthSquared);
    }

    /**
     * Нормализованный вектор
     */
    get Normalized() {
        const length = this.Length;
        if (length < MyPoint.EPSILON) {
            return new MyPoint(0, 0);
        }
        return new MyPoint(this.x / length, this.y / length);
    }

    /**
     * Сложение точек
     * @param {MyPoint} other - другая точка
     * @returns {MyPoint} результат сложения
     */
    add(other) {
        return new MyPoint(this.x + other.x, this.y + other.y);
    }

    /**
     * Вычитание точек
     * @param {MyPoint} other - другая точка
     * @returns {MyPoint} результат вычитания
     */
    subtract(other) {
        return new MyPoint(this.x - other.x, this.y - other.y);
    }

    /**
     * Умножение на скаляр
     * @param {number} scalar - скаляр
     * @returns {MyPoint} результат умножения
     */
    multiply(scalar) {
        return new MyPoint(this.x * scalar, this.y * scalar);
    }

    /**
     * Деление на скаляр
     * @param {number} scalar - скаляр
     * @returns {MyPoint} результат деления
     */
    divide(scalar) {
        if (Math.abs(scalar) < MyPoint.EPSILON) {
            throw new Error("Деление на ноль");
        }
        return new MyPoint(this.x / scalar, this.y / scalar);
    }

    /**
     * Скалярное произведение
     * @param {MyPoint} other - другая точка
     * @returns {number} скалярное произведение
     */
    dot(other) {
        return this.x * other.x + this.y * other.y;
    }

    /**
     * Векторное произведение (2D)
     * @param {MyPoint} other - другая точка
     * @returns {number} векторное произведение
     */
    cross(other) {
        return this.x * other.y - this.y * other.x;
    }

    /**
     * Расстояние до другой точки
     * @param {MyPoint} other - другая точка
     * @returns {number} расстояние
     */
    distanceTo(other) {
        return this.subtract(other).Length;
    }

    /**
     * Квадрат расстояния до другой точки
     * @param {MyPoint} other - другая точка
     * @returns {number} квадрат расстояния
     */
    distanceSquaredTo(other) {
        return this.subtract(other).LengthSquared;
    }

    /**
     * Расстояние до другой точки (метод Dist для совместимости с C#)
     * @param {MyPoint} other - другая точка
     * @returns {number} расстояние
     */
    dist(other) {
        return this.distanceTo(other);
    }

    /**
     * Статический метод для вычисления расстояния между двумя точками
     * @param {MyPoint} a - первая точка
     * @param {MyPoint} b - вторая точка
     * @returns {number} расстояние между точками
     */
    static distance(a, b) {
        return a.distanceTo(b);
    }

    /**
     * Проверка равенства точек
     * @param {MyPoint} other - другая точка
     * @returns {boolean} true если точки равны
     */
    equals(other) {
        if (!other) return false;
        return Math.abs(this.x - other.x) < MyPoint.EPSILON && 
               Math.abs(this.y - other.y) < MyPoint.EPSILON;
    }

    /**
     * Строковое представление точки
     * @returns {string} строка в формате "(x, y)"
     */
    toString() {
        return `(${this.x.toFixed(2)}, ${this.y.toFixed(2)})`;
    }

    /**
     * Создание точки из объекта
     * @param {Object} obj - объект с x и y свойствами
     * @returns {MyPoint} новая точка
     */
    static fromObject(obj) {
        return new MyPoint(obj.x || 0, obj.y || 0);
    }

    /**
     * Создание точки из массива
     * @param {Array} arr - массив [x, y]
     * @returns {MyPoint} новая точка
     */
    static fromArray(arr) {
        return new MyPoint(arr[0] || 0, arr[1] || 0);
    }

    /**
     * Преобразование в массив
     * @returns {Array} массив [x, y]
     */
    toArray() {
        return [this.x, this.y];
    }

    /**
     * Преобразование в объект
     * @returns {Object} объект {x, y}
     */
    toObject() {
        return { x: this.x, y: this.y };
    }

    /**
     * Клонирование точки
     * @returns {MyPoint} новая точка с теми же координатами
     */
    clone() {
        return new MyPoint(this.x, this.y);
    }

    /**
     * Поворот точки вокруг центра
     * @param {number} angle - угол в радианах
     * @param {MyPoint} center - центр поворота (по умолчанию начало координат)
     * @returns {MyPoint} повернутая точка
     */
    rotate(angle, center = MyPoint.ZERO) {
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const dx = this.x - center.x;
        const dy = this.y - center.y;
        
        return new MyPoint(
            center.x + dx * cos - dy * sin,
            center.y + dx * sin + dy * cos
        );
    }

    /**
     * Масштабирование относительно центра
     * @param {number} scaleX - масштаб по X
     * @param {number} scaleY - масштаб по Y
     * @param {MyPoint} center - центр масштабирования (по умолчанию начало координат)
     * @returns {MyPoint} масштабированная точка
     */
    scale(scaleX, scaleY, center = MyPoint.ZERO) {
        return new MyPoint(
            center.x + (this.x - center.x) * scaleX,
            center.y + (this.y - center.y) * scaleY
        );
    }
}

export default MyPoint;